/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-08
 */

// Add third-party dependencies.
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Divider,
  Link,
} from '@mui/material';
import {
  Edit,
  Email,
  Person,
  Schedule,
  Language,
  CheckCircle,
  Cancel,
  Web,
  LinkedIn,
  Instagram,
  Facebook,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

// Add local dependencies.
import type { RootState } from '../../store';
import { getSafeDisplayName } from '../../utils/security';
import { useSessionAuth } from '../../hooks/useSessionAuth';
import ProfileSkeleton from '../common/ProfileSkeleton';
import UserAvatar from '../common/UserAvatar';

/**
 * Props for the UserProfile component.
 */
interface UserProfileProps {
  /**
   * Callback function called when the edit button is clicked.
   */
  onEditClick?: () => void;
  /**
   * Callback function called when the avatar is clicked.
   */
  onAvatarClick?: () => void;
}

/**
 * UserProfile component that displays current user information in a read-only format.
 * Shows user details like name, email, phone, verification status, and account settings.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The user profile display component
 *
 * @example
 * return (
 *   <UserProfile onEditClick={() => setEditMode(true)} />
 * );
 */
const UserProfile: React.FC<UserProfileProps> = ({
  onEditClick,
  onAvatarClick,
}) => {
  const { t } = useTranslation('common');
  const user = useSelector((state: RootState) => state.auth.user);
  const { isLoading } = useSessionAuth();

  // Show skeleton while loading user data
  if (isLoading) {
    return <ProfileSkeleton showEditButton={!!onEditClick} />;
  }

  if (!user) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No user information available
        </Typography>
      </Paper>
    );
  }

  /**
   * Formats a date string for display.
   * @param dateString - The date string to format
   * @returns Formatted date string or 'Not available'
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  /**
   * Gets the verification status chip for email or phone.
   * @param isVerified - Whether the item is verified
   * @param verifiedAt - When the item was verified
   * @returns JSX element for the verification status
   */
  const getVerificationChip = (isVerified: boolean, verifiedAt?: string) => {
    if (isVerified) {
      return (
        <Chip
          icon={<CheckCircle />}
          label={`Verified ${verifiedAt ? formatDate(verifiedAt) : ''}`}
          color="success"
          size="small"
          sx={{ ml: 1 }}
        />
      );
    }
    return (
      <Chip
        icon={<Cancel />}
        label="Not verified"
        color="warning"
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header with avatar and edit button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <UserAvatar
          user={user}
          size="large"
          clickable={!!onAvatarClick}
          onClick={onAvatarClick}
        />
        <Box sx={{ flexGrow: 1, ml: 2 }}>
          <Typography variant="h5" gutterBottom>
            {getSafeDisplayName(user.first_name, user.email)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Member since {formatDate(user.created_at)}
          </Typography>
        </Box>
        {onEditClick && (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={onEditClick}
            sx={{ ml: 2 }}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* User Information Sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Personal Information */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Person sx={{ mr: 1 }} />
            Personal Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                First Name
              </Typography>
              <Typography variant="body1">
                {user.first_name || 'Not provided'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Last Name
              </Typography>
              <Typography variant="body1">
                {user.last_name || 'Not provided'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Full Name
              </Typography>
              <Typography variant="body1">
                {user.full_name ||
                  `${user.first_name} ${user.last_name}`.trim() ||
                  'Not available'}
              </Typography>
            </Box>

            {(user.profile?.title || user.profile?.company) && (
              <>
                {user.profile?.title && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Title
                    </Typography>
                    <Typography variant="body1">
                      {user.profile.title}
                    </Typography>
                  </Box>
                )}

                {user.profile?.company && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1">
                      {user.profile.company}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {user.profile?.bio && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Bio
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {user.profile.bio}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Contact Information */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Email sx={{ mr: 1 }} />
            Contact Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Email Address
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="body1">{user.email}</Typography>
                {getVerificationChip(
                  user.email_verified,
                  user.email_verified_at
                )}
                {!user.email_verified && (
                  <Link
                    component={RouterLink}
                    to="/verify-email"
                    state={{ email: user.email }}
                    variant="body2"
                    sx={{ ml: 1 }}
                  >
                    {t('profile.resendVerification')}
                  </Link>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Phone Number
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="body1">
                  {user.phone_number || 'Not provided'}
                </Typography>
                {user.phone_number &&
                  getVerificationChip(
                    user.phone_verified,
                    user.phone_verified_at
                  )}
              </Box>
            </Box>

            {user.profile?.website && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Website
                </Typography>
                <Link
                  href={user.profile.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Web sx={{ mr: 1, fontSize: 16 }} />
                  {user.profile.website}
                </Link>
              </Box>
            )}
          </Box>
        </Box>

        {/* Social Media */}
        {(user.profile?.linkedin ||
          user.profile?.instagram ||
          user.profile?.facebook) && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Web sx={{ mr: 1 }} />
              Social Media
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {user.profile?.linkedin && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    LinkedIn
                  </Typography>
                  <Link
                    href={user.profile.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <LinkedIn sx={{ mr: 1, fontSize: 16 }} />
                    {user.profile.linkedin}
                  </Link>
                </Box>
              )}

              {user.profile?.instagram && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Instagram
                  </Typography>
                  <Link
                    href={user.profile.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Instagram sx={{ mr: 1, fontSize: 16 }} />
                    {user.profile.instagram}
                  </Link>
                </Box>
              )}

              {user.profile?.facebook && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Facebook
                  </Typography>
                  <Link
                    href={user.profile.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Facebook sx={{ mr: 1, fontSize: 16 }} />
                    {user.profile.facebook}
                  </Link>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Account Settings */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Schedule sx={{ mr: 1 }} />
            Account Settings
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Timezone
              </Typography>
              <Typography variant="body1">
                {user.timezone || 'Not set'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Language
              </Typography>
              <Typography variant="body1">
                {user.language || 'Not set'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Marketing Emails
              </Typography>
              <Chip
                label={user.marketing_emails ? 'Enabled' : 'Disabled'}
                color={user.marketing_emails ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        {/* Account Status */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Language sx={{ mr: 1 }} />
            Account Status
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Account Status
              </Typography>
              <Chip
                label={user.is_active ? 'Active' : 'Inactive'}
                color={user.is_active ? 'success' : 'error'}
                size="small"
              />
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Terms Accepted
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Chip
                  label={user.terms_accepted ? 'Accepted' : 'Not accepted'}
                  color={user.terms_accepted ? 'success' : 'warning'}
                  size="small"
                />
                {user.terms_accepted_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    on {formatDate(user.terms_accepted_at)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Privacy Policy Accepted
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Chip
                  label={user.privacy_accepted ? 'Accepted' : 'Not accepted'}
                  color={user.privacy_accepted ? 'success' : 'warning'}
                  size="small"
                />
                {user.privacy_accepted_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    on {formatDate(user.privacy_accepted_at)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Account Timestamps */}
      <Divider sx={{ my: 3 }} />
      <Box>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Account Created
            </Typography>
            <Typography variant="body1">
              {formatDate(user.created_at)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Last Updated
            </Typography>
            <Typography variant="body1">
              {formatDate(user.updated_at)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserProfile;
